import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { live } from '../data/live';
import { useChargePoints, useConnectorMutations } from '../lib/queries';
import {
  CONNECTOR_LABEL, ConnectorStatus, OCCUPIED_STATUSES, connectorTone, isWaiting,
} from '../data/types';
import type { Connector, ConnectorStatus as CStatus, ChargePoint } from '../data/types';
import { Chip, Empty, Loading, Pill, Tile } from '../components/ui';
import { qrSvg } from '../lib/qr';
import { errorText, loadFailedHint } from '../lib/errors';
import { BFF } from '../data/live';

type Row = { station: ChargePoint; connector: Connector; waiting: boolean };

/**
 * The sticker that goes on the gun.
 *
 * A driver scans it and starts a charge on that exact connector, which is the
 * whole point: a QR per charge point would still leave them choosing "left or
 * right?" in front of two identical couplers, and choosing wrong starts someone
 * else's car.
 *
 * The payload is an https URL rather than a custom scheme, so a phone camera
 * that knows nothing about Spark EV still does something useful with it — it
 * opens a page that offers the app. A `sparkev://` code would simply fail to
 * scan for anyone who has not installed it yet, which is exactly the person
 * standing at a public charger.
 */
function GunQr({ row, onClose }: { row: Row; onClose: () => void }) {
  const url = `${BFF}/c/${encodeURIComponent(row.station.id)}/${row.connector.id}`;
  const title = `${row.station.name} - C${row.connector.id}`;

  return (
    <div
      role="dialog" aria-label="Connector QR"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
        display: 'grid', placeItems: 'center', padding: 16, zIndex: 40,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 380 }}>
      <div className="li-title" style={{ fontSize: 15 }}>{title}</div>
      <div className="li-sub" style={{ marginBottom: 12 }}>
        {row.connector.label ? row.connector.label + ' - ' : ''}
        {row.connector.type ?? 'coupler type not set'}
      </div>

      {/* White on black regardless of theme: a scanner needs the contrast and
          an inverted code does not read. */}
      <div style={{ background: '#fff', padding: 14, borderRadius: 12, lineHeight: 0,
                    display: 'flex', justifyContent: 'center' }}
           dangerouslySetInnerHTML={{ __html: qrSvg(url, 232) }} />

      <div className="li-sub mono" style={{ marginTop: 10, wordBreak: 'break-all' }}>{url}</div>

      <div className="note" style={{ marginTop: 12 }}>
        Print this and put it on the gun itself, not on the cabinet. The code
        names one connector, and a driver who scans the wrong sticker starts a
        charge on the wrong car.
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button className="btn" style={{ flex: 1 }} onClick={() => window.print()}>Print</button>
        <button className="btn ghost" style={{ flex: 1 }} onClick={onClose}>Close</button>
      </div>
      </div>
    </div>
  );
}

/* Offered if the list cannot be fetched. The network defines the couplers and
   this screen asks for them; these seven are what it had when this build was
   made, kept so the editor is never an empty dropdown. */
const FALLBACK_TYPES = [
  'Type 1', 'Type 2', 'CCS1', 'CCS2', 'CHAdeMO', 'GB/T', 'Tesla/NACS',
];

/** The couplers, from the server. */
function useConnectorTypes(): string[] {
  const { data } = useQuery({
    queryKey: ['connector-types'],
    queryFn: () => live.connectorTypes(),
    staleTime: 10 * 60_000,
  });
  const live_ = (data ?? []).filter((t) => t.active).map((t) => t.label || t.code);
  return live_.length ? live_ : FALLBACK_TYPES;
}
const PER_PAGE = 12;
/* Every state a bay can be in, in the order an operator scans for them: what
   is usable, what is busy, what needs a visit. */
const STATUSES = ConnectorStatus.options;


/**
 * What a bay is, as opposed to what it is doing.
 *
 * Status is the charger's to report and is never written from here. Type and
 * power are supposed to be the charger's too, but OCPP 1.6 carries them in
 * StatusNotification only as optional fields and plenty of firmware sends
 * neither — which is why a live bay can read "type not reported". These are the
 * operator's answers to that, laid over whatever the hardware does say.
 */
function EditConnector({ row, pending, error, onClose, onSave, onRemove }: {
  row: Row;
  pending: boolean;
  /** The server's refusal of a save or a remove. Without it the dialog just
   *  stayed open, which reads as the button doing nothing. */
  error: string | null;
  onClose: () => void;
  onSave: (v: {
    label: string | null; connectorType: string | null;
    powerType: 'AC' | 'DC' | null; maxWatts: number | null;
  }) => void;
  onRemove: () => void;
}) {
  const c = row.connector;
  const types = useConnectorTypes();
  const [label, setLabel] = useState(c.label ?? '');
  const [type, setType] = useState(c.type ?? '');
  const [current, setCurrent] = useState<'' | 'AC' | 'DC'>(c.current ?? '');
  const [kw, setKw] = useState(c.powerKw == null ? '' : String(c.powerKw));

  const kwN = kw.trim() === '' ? null : Number(kw);
  const bad = kwN != null && !(Number.isFinite(kwN) && kwN > 0)
    ? 'Power must be a number greater than zero.' : null;

  return (
    <div
      role="dialog" aria-label="Edit connector"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
        display: 'grid', placeItems: 'center', padding: 16, zIndex: 40,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 420 }}>
        <div className="li-title" style={{ fontSize: 15 }}>
          Connector {c.id} on {row.station.name}
        </div>
        <div className="li-sub" style={{ marginBottom: 12 }}>
          {c.reported === false
            ? 'The charger has never mentioned this bay. It shows here because you declared it.'
            : 'The charger reports this bay. What it does not report, you can fill in.'}
        </div>

        <label className="li-sub" htmlFor="cn-label">Label (optional)</label>
        <input id="cn-label" className="field" value={label} placeholder="Left gun"
               onChange={(e) => setLabel(e.target.value)} style={{ marginBottom: 10 }} />

        <label className="li-sub" htmlFor="cn-type">Coupler type</label>
        <select id="cn-type" className="field" value={type} style={{ marginBottom: 10 }}
                onChange={(e) => setType(e.target.value)}>
          <option value="">- not set -</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
          {/* A bay already set to a retired coupler keeps reading as it. */}
          {type && !types.includes(type) && <option value={type}>{type}</option>}
        </select>

        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <label className="li-sub" htmlFor="cn-cur">AC or DC</label>
            <select id="cn-cur" className="field" value={current}
                    onChange={(e) => setCurrent(e.target.value as '' | 'AC' | 'DC')}>
              <option value="">- not set -</option>
              <option value="AC">AC</option>
              <option value="DC">DC</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label className="li-sub" htmlFor="cn-kw">Power (kW)</label>
            <input id="cn-kw" className="field mono" value={kw} inputMode="decimal"
                   placeholder="not set" onChange={(e) => setKw(e.target.value)} />
          </div>
        </div>

        <div className="note" style={{ marginTop: 10 }}>
          <div className="li-sub">Reported by the charger, and not editable:</div>
          <div className="mono">
            status {c.status}
            {c.reported === false ? ' (never reported)' : ''}
          </div>
          <div className="li-sub" style={{ marginTop: 6 }}>
            A bay's state comes from StatusNotification. Writing it here would be
            overwritten by the charger's next message and would be a lie until
            it was.
          </div>
        </div>

        {(bad || error) && (
          <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 8 }}>
            {bad ?? error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="btn" style={{ flex: 1 }} disabled={!!bad || pending}
                  onClick={() => onSave({
                    label: label.trim() || null,
                    connectorType: type || null,
                    powerType: current || null,
                    maxWatts: kwN == null ? null : Math.round(kwN * 1000),
                  })}>
            {pending ? 'Saving...' : 'Save'}
          </button>
          <button className="btn ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
        </div>
        <button className="btn ghost sm" style={{ width: '100%', marginTop: 8 }}
                disabled={pending} onClick={onRemove}>
          {c.reported === false ? 'Remove this declared bay' : 'Clear what I set'}
        </button>
      </div>
    </div>
  );
}

/** Declare a bay the charger has never mentioned. */
function AddConnector({ points, pending, error, onClose, onSave }: {
  points: ChargePoint[];
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (v: { cpId: string; connectorId: number }) => void;
}) {
  const [cpId, setCpId] = useState(points[0]?.id ?? '');
  const [num, setNum] = useState('');
  const cp = points.find((p) => p.id === cpId);
  const n = Number(num);
  const taken = !!cp?.connectors.some((c) => c.id === n);
  const bad = !cpId ? 'Choose a charge point.'
    : !(Number.isInteger(n) && n >= 1) ? 'A connector id is a whole number, 1 or more.'
      : taken ? `Connector ${n} already exists on that charge point.` : null;

  return (
    <div
      role="dialog" aria-label="Add connector"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
        display: 'grid', placeItems: 'center', padding: 16, zIndex: 40,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 420 }}>
        <div className="li-title" style={{ fontSize: 15 }}>Add a connector</div>
        <div className="li-sub" style={{ marginBottom: 12 }}>
          For a bay the charger has not reported. A two-gun unit that has only
          ever mentioned gun 1 is worth seeing whole.
        </div>

        <label className="li-sub" htmlFor="ac-cp">Charge point</label>
        <select id="ac-cp" className="field" value={cpId} style={{ marginBottom: 10 }}
                onChange={(e) => setCpId(e.target.value)}>
          {points.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.connectors.length} known)
            </option>
          ))}
        </select>

        <label className="li-sub" htmlFor="ac-n">OCPP connector id</label>
        <input id="ac-n" className="field mono" value={num} inputMode="numeric"
               placeholder="2" onChange={(e) => setNum(e.target.value)} />
        <div className="li-sub faint" style={{ marginTop: 4 }}>
          The number the charger uses in StartTransaction, and the one a bay's QR
          label will resolve to. Not a display position.
        </div>

        {(bad || error) && (
          <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 8 }}>
            {bad ?? error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="btn" style={{ flex: 1 }} disabled={!!bad || pending}
                  onClick={() => onSave({ cpId, connectorId: n })}>
            {pending ? 'Adding...' : 'Add connector'}
          </button>
          <button className="btn ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function Connectors() {
  const { data: res, isLoading, error: loadError } = useChargePoints();
  const data = res?.items;
  const [status, setStatus] = useState<'all' | CStatus>('all');
  const [stationId, setStationId] = useState('all');
  const [page, setPage] = useState(1);
  const { save, remove } = useConnectorMutations();
  const [qr, setQr] = useState<Row | null>(null);
  const [editing, setEditing] = useState<Row | null>(null);
  const [adding, setAdding] = useState(false);
  /* Shared by the edit and add dialogs — only one is ever open — and cleared
     whenever either opens, so an old refusal does not greet a new dialog. */
  const [dialogError, setDialogError] = useState<string | null>(null);
  const failed = (e: Error) => setDialogError(errorText(e));

  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    return data.flatMap((s) =>
      s.connectors.map((c) => ({ station: s, connector: c, waiting: isWaiting(s, c) })));
  }, [data]);

  if (isLoading) return <Loading />;
  // A failed load is said, not spun on forever.
  if (!data) return <Empty title="Connectors are not available." hint={loadFailedHint(loadError)} />;

  const filtered = rows.filter((r) => {
    if (status !== 'all' && r.connector.status !== status) return false;
    if (stationId !== 'all' && r.station.id !== stationId) return false;
    return true;
  });

  // Paged at the same 12 the label PDF puts on a sheet, so a page of the table
  // is a page of labels.
  const pageCount = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, pageCount);
  const from = (current - 1) * PER_PAGE;
  const shown = filtered.slice(from, from + PER_PAGE);

  const counts = {
    all: rows.length,
    ...Object.fromEntries(STATUSES.map((s) => [s, rows.filter((r) => r.connector.status === s).length])),
  } as Record<string, number>;
  const hidden = rows.filter((r) => r.station.blocked).length;
  // A bay with a cable in it, whether or not energy is moving yet.
  const inUse = rows.filter((r) => OCCUPIED_STATUSES.includes(r.connector.status)).length;

  // Changing a filter while on page 2 would otherwise strand you on an empty page.
  const setFilter = (fn: () => void) => { fn(); setPage(1); };

  return (
    <>
      <div className="tiles">
        <Tile k="Connectors" v={rows.length} d={`on ${data.length} charge points`} />
        <Tile k="Available" v={counts.available ?? 0} d="ready to charge" />
        <Tile k="In use" v={inUse} d={inUse ? 'a driver is on them' : 'none in use'} />
        <Tile k="Faulted" v={counts.faulted ?? 0} d={counts.faulted ? 'need a visit' : 'none reported'} />
        <Tile k="Hidden" v={hidden} d={hidden ? 'on blocked stations' : 'all reachable'} />
      </div>

      <div className="toolbar">
        <div className="chips">
          <Chip label={<>All <span className="mono">{counts.all}</span></>}
                selected={status === 'all'} onClick={() => setFilter(() => setStatus('all'))} />
          {/* Only states actually present get a chip: nine of them, most reading
              zero, is a wall rather than a filter. The selected one always stays
              so the chip you clicked cannot vanish under you. */}
          {STATUSES.filter((s) => counts[s] || status === s).map((s) => (
            <Chip
              key={s}
              selected={status === s}
              onClick={() => setFilter(() => setStatus(s))}
              label={<>{CONNECTOR_LABEL[s]} <span className="mono">{counts[s] ?? 0}</span></>}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            className="field"
            style={{ width: 'auto' }}
            value={stationId}
            onChange={(e) => setFilter(() => setStationId(e.target.value))}
          >
            <option value="all">All charge points</option>
            {data.map((s) => (
              <option key={s.id} value={s.id}>{s.name}{s.blocked ? ' (blocked)' : ''}</option>
            ))}
          </select>
          <button className="btn sm" onClick={() => { setDialogError(null); setAdding(true); }}>Add connector</button>
        </div>
      </div>

      {!shown.length ? (
        <Empty title="No connectors match this filter." hint="Try a different status, or All charge points." />
      ) : (
        <>
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>Charger</th><th>ChargePoint</th><th>Type</th>
                  <th>Power</th><th>Status</th><th />
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={`${r.station.id}-${r.connector.id}`} className={r.station.blocked ? 'off' : undefined}>
                    <td><span className="conn-pill">C{r.connector.id}</span></td>
                    <td>
                      {r.station.name}
                      {r.station.blocked && <Pill tone="off">Blocked</Pill>}
                    </td>
                    <td>
                      {r.connector.label && <div>{r.connector.label}</div>}
                      {r.connector.type ?? <span className="faint">not reported</span>}
                    </td>
                    <td className="mono">
                      {r.connector.powerKw != null
                        ? `${r.connector.powerKw} kW`
                        : <span className="faint">-</span>}
                      {r.connector.current ? ` · ${r.connector.current}` : ''}
                    </td>
                    <td>
                      <Pill tone={r.waiting ? 'mid' : connectorTone(r.connector.status)}>
                        {r.waiting ? 'Waiting' : CONNECTOR_LABEL[r.connector.status]}
                      </Pill>
                      {r.waiting && <div className="li-sub">shared power</div>}
                    </td>
                    <td className="act">
                      <button className="btn sm ghost" onClick={() => setQr(r)}>QR</button>
                      <button className="btn sm ghost" onClick={() => { setDialogError(null); setEditing(r); }}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length > PER_PAGE && (
            <div className="pager">
              <span className="li-sub">
                {from + 1}–{Math.min(from + PER_PAGE, filtered.length)} of {filtered.length}
              </span>
              <div className="pg-btns">
                <button className="btn sm ghost" disabled={current <= 1} onClick={() => setPage(current - 1)}>
                  Previous
                </button>
                <span className="mono li-sub">{current} / {pageCount}</span>
                <button className="btn sm ghost" disabled={current >= pageCount} onClick={() => setPage(current + 1)}>
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
      {qr && <GunQr row={qr} onClose={() => setQr(null)} />}
      {editing && (
        <EditConnector
          row={editing}
          pending={save.isPending || remove.isPending}
          error={dialogError}
          onClose={() => setEditing(null)}
          onSave={(v) => { setDialogError(null); save.mutate(
            { cpId: editing.station.id, connectorId: editing.connector.id, ...v },
            { onSuccess: () => setEditing(null), onError: failed },
          ); }}
          onRemove={() => { setDialogError(null); remove.mutate(
            { cpId: editing.station.id, connectorId: editing.connector.id },
            { onSuccess: () => setEditing(null), onError: failed },
          ); }}
        />
      )}

      {adding && (
        <AddConnector
          points={data}
          pending={save.isPending}
          error={dialogError}
          onClose={() => setAdding(false)}
          onSave={(v) => { setDialogError(null); save.mutate(
            { ...v, declared: true },
            { onSuccess: () => setAdding(false), onError: failed },
          ); }}
        />
      )}
    </>
  );
}
